import React, { ReactNode } from 'react'
import { useFormikContext } from 'formik'
import { t } from '../../i18n'
import FillGlyph from '../shared/dosing_modes/FillGlyph'
import { FillLevel } from '../shared/dosing_modes/fillLevels'
import { CheckboxField, SelectField } from '../shared/forms/FormFields'
import { InputField } from './fields'
import {
  FILL_LEVEL_OPTIONS,
  STRATEGY_OPTIONS,
  modeLabel,
  paramLabel,
  paramUnit,
  placeholderLabel
} from './labels'
import {
  LevelNumericKey,
  LevelValues,
  SauceDosingModeValues,
  isNumber
} from './types'

interface ParameterRow {
  key: LevelNumericKey
  label: string
  unit: string
  step: string
  placeholder?: string
}

const numericRow = (
  key: LevelNumericKey,
  step: string,
  withPlaceholder = false
): ParameterRow => ({
  key,
  label: paramLabel(key),
  unit: paramUnit(key),
  step,
  placeholder: withPlaceholder ? placeholderLabel(key) : undefined
})

const BASE_ROWS = [
  numericRow('fast_pressure', '0.01', true),
  numericRow('slow_pressure', '0.01', true),
  numericRow('stop_advance_grams', '1'),
  numericRow('max_dosing_duration_ms', '1'),
  numericRow('no_change_max_time_ms', '1'),
  numericRow('no_change_amount', '1')
]

const EMERGENCY_ROWS = [
  numericRow('emergency_pressure', '0.01'),
  numericRow('max_emergency_duration_ms', '1'),
  numericRow('emergency_residual_delivery_amount', '1')
]

const LABELS = {
  emergencyEnabled: paramLabel('emergency_enabled'),
  emergencyStrategy: paramLabel('emergency_strategy'),
  selectStrategy: placeholderLabel('select_strategy')
}

const fillRanges = (
  high: unknown,
  medium: unknown
): Record<FillLevel, string> | null => {
  if (!isNumber(high) || !isNumber(medium) || medium >= high) return null

  return {
    high: t('sauce_dosing_modes.fill_ranges.high', { from: high }),
    medium: t('sauce_dosing_modes.fill_ranges.medium', {
      from: medium,
      to: high
    }),
    low: t('sauce_dosing_modes.fill_ranges.low', { to: medium })
  }
}

interface LevelCellContext {
  level: LevelValues
  index: number
  ariaLabel: string
}

type LevelCell = (context: LevelCellContext) => ReactNode

const FILL_LEVEL_LABELS = Object.fromEntries(
  FILL_LEVEL_OPTIONS.map((option) => [option.value, option.label])
) as Record<FillLevel, string>

const useModeValues = () => useFormikContext<SauceDosingModeValues>().values

const LevelRow = ({ header, cell }: { header: string; cell: LevelCell }) => {
  const { levels } = useModeValues()

  return (
    <tr>
      <th scope="row" className="dosing-levels__param">
        {header}
      </th>
      {levels.map((level, index) => (
        <td key={level.fill_level} className="dosing-levels__cell">
          {cell({
            level,
            index,
            ariaLabel: `${FILL_LEVEL_LABELS[level.fill_level]}: ${header}`
          })}
        </td>
      ))}
    </tr>
  )
}

const NumericRow = ({
  row,
  emergencyOnly = false
}: {
  row: ParameterRow
  emergencyOnly?: boolean
}) => (
  <LevelRow
    header={row.label}
    cell={({ level, index, ariaLabel }) => (
      <InputField
        name={`levels.${index}.${row.key}`}
        step={row.step}
        placeholder={row.placeholder}
        unit={row.unit}
        ariaLabel={ariaLabel}
        disabled={emergencyOnly && !level.emergency_enabled}
        inverted={false}
      />
    )}
  />
)

const LevelTable = ({
  title,
  withRanges,
  children
}: {
  title: string
  withRanges: boolean
  children: ReactNode
}) => {
  const { high_fill_min_percent, medium_fill_min_percent } = useModeValues()
  const ranges = withRanges
    ? fillRanges(high_fill_min_percent, medium_fill_min_percent)
    : null

  return (
    <table className="dosing-levels">
      <thead>
        <tr>
          <th scope="col" className="dosing-levels__title">
            {title}
          </th>

          {FILL_LEVEL_OPTIONS.map(({ value: level, label }) => (
            <th key={level} scope="col" className="dosing-levels__cell">
              <div className="dosing-levels__level">
                <span className="dosing-levels__level-name">
                  <FillGlyph level={level} />
                  {label}
                </span>
              </div>
              {ranges && (
                <div className="dosing-levels__range">{ranges[level]}</div>
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  )
}

export const BaseLevelParameters = () => (
  <LevelTable title={modeLabel('level_parameters')} withRanges>
    {BASE_ROWS.map((row) => (
      <NumericRow key={row.key} row={row} />
    ))}
  </LevelTable>
)

export const EmergencyLevelParameters = () => (
  <LevelTable title={modeLabel('emergency_dosing')} withRanges={false}>
    <LevelRow
      header={LABELS.emergencyEnabled}
      cell={({ index, ariaLabel }) => (
        <CheckboxField
          name={`levels.${index}.emergency_enabled`}
          ariaLabel={ariaLabel}
          variant="switch"
        />
      )}
    />
    <LevelRow
      header={LABELS.emergencyStrategy}
      cell={({ level, index, ariaLabel }) => (
        <SelectField
          name={`levels.${index}.emergency_strategy`}
          options={STRATEGY_OPTIONS}
          placeholder={LABELS.selectStrategy}
          ariaLabel={ariaLabel}
          disabled={!level.emergency_enabled}
          inverted={false}
        />
      )}
    />
    {EMERGENCY_ROWS.map((row) => (
      <NumericRow key={row.key} row={row} emergencyOnly />
    ))}
  </LevelTable>
)
